// Single source of truth for product / cuisine categories used across the app.
// Keep in sync with backend `CATEGORY_KEYS` in /app/backend/server.py.
//
// Phase 1 (no-emoji rule): each category has a `name` (pure text) and an
// `icon` slug that maps to a <CategoryIcon /> component. The legacy `label`
// field is kept for backwards-compatibility with any consumer that still
// renders `.label` directly — it now contains ONLY text (no emoji).
export const CATEGORY_OPTIONS = [
  { value: "street_food",    name: "Street Food",    icon: "street_food" },
  { value: "bbq",            name: "BBQ",            icon: "bbq" },
  { value: "seafood",        name: "Seafood",        icon: "seafood" },
  { value: "mexican",        name: "Mexican",        icon: "mexican" },
  { value: "soul_food",      name: "Soul Food",      icon: "soul_food" },
  { value: "desserts",       name: "Desserts",       icon: "desserts" },
  { value: "drinks",         name: "Drinks",         icon: "drinks" },
  { value: "snacks",         name: "Snacks",         icon: "snacks" },
  { value: "plates",         name: "Plates",         icon: "plates" },
  { value: "meal_prep",      name: "Meal Prep",      icon: "meal_prep" },
  { value: "pop_ups",        name: "Pop Ups",        icon: "pop_ups" },
  { value: "cultural",       name: "Cultural",       icon: "cultural" },
  { value: "wholesale",      name: "Wholesale",      icon: "wholesale" },
  { value: "food_trucks",    name: "Food Trucks",    icon: "food_trucks" },
  { value: "food_carts",     name: "Food Carts",     icon: "food_carts" },
  { value: "fundraisers",    name: "Fundraisers",    icon: "fundraisers" },
  { value: "catering",       name: "Catering",       icon: "catering" },
  { value: "home_bakery",    name: "Home Bakery",    icon: "home_bakery" },
  { value: "vegan",          name: "Vegan",          icon: "vegan" },
  { value: "breakfast",      name: "Breakfast",      icon: "breakfast" },
  { value: "family_owned",   name: "Family Owned",   icon: "family_owned" },
  { value: "late_night",     name: "Late Night",     icon: "late_night" },
  { value: "homemade",       name: "Homemade",       icon: "homemade" },
  { value: "wings",          name: "Wings",          icon: "wings" },
  { value: "coffee",         name: "Coffee",         icon: "coffee" },
  { value: "farmers_market", name: "Farmers Market", icon: "farmers_market" },
  { value: "hibachi",        name: "Hibachi",        icon: "hibachi" },
].map((c) => ({ ...c, label: c.name })); // legacy `.label` = plain text

// Quick lookup: is this value one of the built-in presets?
const _PRESET_VALUES = new Set(CATEGORY_OPTIONS.map((c) => c.value));
export function isPresetCategory(value) {
  return _PRESET_VALUES.has(value);
}

// Look up a preset row by value (or undefined for custom categories).
export function getCategoryMeta(value) {
  return CATEGORY_OPTIONS.find((c) => c.value === value);
}

// Convert a free-text label entered by a vendor into a stable "value" slug:
//   "Spicy Korean Tacos" → "spicy_korean_tacos"
export function slugifyCategory(text) {
  return String(text || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

// Render a category value (preset or custom) as a human label for chips/badges.
export function formatCategoryLabel(value) {
  const preset = getCategoryMeta(value);
  if (preset) return preset.name;
  // Custom: turn snake_case → Title Case
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export const WEEKDAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];
